import React, { useEffect } from "react";
import { GuideLayout } from "./GuideLayout";
import { trackEvent } from "../../analytics";

const sections = [
  {
    id: "definition",
    heading: "What is double materiality?",
    body: [
      "Double materiality is a sustainability reporting approach that looks at two perspectives: how a company impacts people and the environment, and how sustainability issues affect the company's financial performance, risks, and opportunities.",
      "Under CSRD and ESRS, double materiality helps companies decide which sustainability topics should be reported because they are significant for stakeholders, enterprise value, or both."
    ]
  },
  {
    id: "impact-financial",
    heading: "Impact materiality and financial materiality",
    body: [
      "Impact materiality focuses on actual or potential effects the company has on people, communities, workers, value chains, and the environment. Financial materiality focuses on how sustainability matters may affect revenue, costs, access to capital, asset values, operations, reputation, or resilience.",
      "A topic can be material from either perspective. For example, climate change can be material because a company contributes to emissions and because transition risks, regulation, or physical climate risks can affect financial performance."
    ]
  },
  {
    id: "process",
    heading: "How to run a double materiality assessment",
    body: [
      "A practical assessment should combine stakeholder input, internal data, sector context, risk analysis, and management judgment. The goal is not just to produce a matrix, but to create a defensible basis for ESG strategy and reporting.",
      "Teams should document assumptions, scoring logic, evidence sources, consulted stakeholders, topic thresholds, and final decisions."
    ],
    bullets: [
      "Create a long list of potential sustainability topics",
      "Consult internal and external stakeholders",
      "Score impact severity, scope, likelihood, and remediability",
      "Score financial magnitude, likelihood, and time horizon",
      "Validate results with leadership and topic owners",
      "Connect material topics to ESRS disclosure requirements"
    ]
  },
  {
    id: "common-mistakes",
    heading: "Common mistakes to avoid",
    body: [
      "Many companies treat double materiality as a one-time survey or a visual matrix exercise. That creates weak reporting foundations. A stronger approach links materiality to data ownership, risk management, targets, and disclosure quality.",
      "Another common mistake is failing to document why topics were included or excluded. This can create problems during assurance, investor review, or customer due diligence."
    ]
  },
  {
    id: "software",
    heading: "How Ecovanta supports double materiality",
    body: [
      "Ecovanta helps teams structure materiality topics, score impact and financial dimensions, identify gaps, and convert materiality results into practical reporting workflows.",
      "The platform is designed to support sustainability, finance, operations, HR, and leadership teams as they move from fragmented inputs to decision-ready ESG reporting outputs."
    ]
  }
];

const faqs = [
  {
    question: "What does double materiality mean?",
    answer:
      "Double materiality means assessing both how a company impacts people and the environment and how sustainability issues affect the company's financial position, performance, and resilience."
  },
  {
    question: "Is double materiality required for CSRD?",
    answer:
      "Double materiality is a core concept under CSRD and ESRS reporting. It helps determine which sustainability topics and disclosures are material for the company."
  },
  {
    question: "Who should be involved in a double materiality assessment?",
    answer:
      "Sustainability, finance, risk, HR, operations, procurement, legal, leadership, and selected external stakeholders may all contribute evidence and judgment."
  },
  {
    question: "Can AI help with double materiality?",
    answer:
      "AI can help organize stakeholder inputs, summarize evidence, identify gaps, and draft reviewable explanations. Final materiality judgments should remain human-reviewed and well documented."
  }
];

export default function DoubleMaterialityGuide() {
  useEffect(() => {
    trackEvent("guide_view", {
      guide: "double_materiality",
      path: window.location.pathname
    });
  }, []);

  return (
    <GuideLayout
      eyebrow="Double Materiality Guide"
      title="Double Materiality Assessment: A Practical Guide for ESG and CSRD Reporting"
      description="Understand double materiality, impact materiality, financial materiality, and how companies can run a defensible assessment for CSRD and ESRS reporting."
      sections={sections}
      faqs={faqs}
      ctaTitle="Run a clearer double materiality process with Ecovanta"
      ctaText="Ecovanta helps companies structure materiality topics, score impact and financial dimensions, document decisions, and turn results into ESG reporting actions."
      onCtaClick={() =>
        trackEvent("demo_click", {
          source: "double_materiality_guide",
          path: window.location.pathname
        })
      }
    />
  );
}